/**
 * component-size-chart.js -- Size Chart Modal Web Component for Mirai Online (Phase 3)
 *
 * <size-chart-modal> custom element:
 *  - Opens a centered modal (server-rendered size table) when a [data-size-guide-open] trigger is clicked
 *  - Focus trap + inert background (same approach as cart-drawer), Escape / × / backdrop close
 *  - Restores focus to the trigger on close
 *  - opacity+scale animation via --open class; reduced-motion handled in CSS
 *  - Theme editor compatible via shopify:section:load reinit
 *  - Light DOM only (no Shadow DOM)
 */

class SizeChartModal extends HTMLElement {
  constructor() {
    super();
    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundOpenClick = this._handleOpenClick.bind(this);
    this._isOpen = false;
    this._lastFocused = null;
    this._releaseFocusTrap = null;
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    this._teardown();
  }

  _init() {
    this._modal = this.querySelector('[data-size-chart]');
    this._closeBtn = this.querySelector('[data-size-chart-close]');

    // Open triggers live in the sibling variant selector, not inside this element —
    // scope the lookup to the enclosing product section.
    const section = this.closest('.main-product') || document;
    this._openTriggers = Array.from(section.querySelectorAll('[data-size-guide-open]'));
    this._openTriggers.forEach((btn) => btn.addEventListener('click', this._boundOpenClick));

    if (this._closeBtn) this._closeBtn.addEventListener('click', () => this.close());
    if (this._modal) {
      this._modal.addEventListener('click', (e) => {
        if (e.target === this._modal) this.close();
      });
    }
    document.addEventListener('keydown', this._boundKeydown);
  }

  _teardown() {
    // WR-04: if torn down while the modal is open, fully close first so the
    // background inert (.pdp-variants/.pdp-cart-form) is released, _isOpen is
    // reset, and focus is restored.
    if (this._isOpen) this.close();

    document.removeEventListener('keydown', this._boundKeydown);
    if (this._openTriggers) {
      this._openTriggers.forEach((btn) => btn.removeEventListener('click', this._boundOpenClick));
    }
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  _handleOpenClick(e) {
    this._lastFocused = e.currentTarget;
    this.open();
  }

  open() {
    if (!this._modal || this._isOpen) return;
    this._modal.removeAttribute('hidden');
    void this._modal.offsetWidth; // reflow so the transition runs
    this._modal.classList.add('size-chart-modal--open');
    this._isOpen = true;

    this._setBackgroundInert(true);
    if (this._closeBtn) this._closeBtn.focus();
    this._releaseFocusTrap = this._trapFocus(this._modal);
  }

  close() {
    if (!this._modal || !this._isOpen) return;
    this._modal.classList.remove('size-chart-modal--open');
    this._modal.setAttribute('hidden', '');
    this._isOpen = false;

    this._setBackgroundInert(false);
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
    }
  }

  _handleKeydown(e) {
    if (e.key === 'Escape' && this._isOpen) this.close();
  }

  _setBackgroundInert(on) {
    const section = this.closest('.main-product');
    if (!section) return;
    const targets = Array.from(section.querySelectorAll('.pdp-gallery, .pdp-gallery__dots, .pdp-variants, .pdp-cart-form'));
    targets.forEach((el) => {
      if (on) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  _trapFocus(container) {
    const focusableSelectors =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSelectors));

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    return () => container.removeEventListener('keydown', handleKeydown);
  }
}

if (!customElements.get('size-chart-modal')) {
  customElements.define('size-chart-modal', SizeChartModal);
}
